import BaseSubscriptionModal from 'app/components/subscriptionModals/BaseSubscriptionModal';
import Benefits from 'app/components/subscriptionModals/Benefits';
import postIcon from 'app/images/postsModalGreet.svg';
import { FC } from 'react';
import { SUBSCRIPTION_MODAL_BENEFITS } from './subscriptionModalBenefits';
import SubscriptionExpiredButtons from './SubscriptionExpiredButtons';
import SubscriptionExpiredFooter from './SubscriptionExpiredFooter';
import { useTranslation, Trans } from 'react-i18next';
import { ModalManager } from 'context/ModalRouter';
import AccountOnHold from 'app/components/subscriptionModals/AccountOnHold';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';

type Props = {
  open?: boolean;
  modalManager?: ModalManager;
};

/**
 * The app-wide "you are blocked" modal for a non-trial account.
 *
 * It branches here rather than at the two call sites (SubscriptionModals and
 * the `modalRoutes.SUBSCRIPTION` route in CommonModals) so both of them get the
 * on-hold variant; a switch in SubscriptionModals alone would have left the
 * modal-router entry still selling a plan to a held account.
 *
 * Only `on_hold` diverts. `cancelled`, `trial_ended`, `period_lapsed` and
 * `no_subscription` are all states a plan purchase does resolve, so they keep
 * the expiry copy and the "Choose your plan" CTA exactly as before — as does
 * every legacy account, for which `blockedReason` is always null.
 */
const SubscriptionExpired: FC<Props> = ({ open, modalManager }) => {
  const { t } = useTranslation();
  const { blockedReason } = useSubscriptionInfo();

  if (blockedReason === 'on_hold') {
    return <AccountOnHold open={open} modalManager={modalManager} />;
  }

  return (
    <BaseSubscriptionModal
      open={open}
      modalManager={modalManager}
      classNames={{
        modal: 'tw-max-h-[min(800px,82vh)]',
      }}
      title={
        <div className="tw-text-xl tw-font-medium">
          <Trans
            i18nKey="subscription.expired.title"
            components={{ 1: <strong /> }}
          />
        </div>
      }
      image={<img src={postIcon} alt="Post icon" />}
      buttons={
        <SubscriptionExpiredButtons
          primaryText={t('subscription.expired.choosePlanButton')}
        />
      }
      benefits={
        <Benefits
          title={t('subscription.expired.benefitsTitle')}
          lists={SUBSCRIPTION_MODAL_BENEFITS.posts}
        />
      }
      footer={
        <SubscriptionExpiredFooter
          text={t('subscription.expired.footerText')}
        />
      }
    />
  );
};

export default SubscriptionExpired;
